import { Link } from 'react-router-dom';
import { FILTER, FILTER_TYPES, SECTION_TYPE, filterMeta } from './constants';

/**
 * Structured conditions. Different condition types are ANDed; several of the same type are ORed
 * (e.g. two brands). The same list drives the pinned-product picker, so it can only offer eligible products.
 */
const FilterEditor = ({ filters, onChange, options, sectionType, isRtl }) => {
  const label = (m) => (isRtl ? m.ar : m.en);

  const setRow = (idx, patch) => onChange(filters.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const removeRow = (idx) => onChange(filters.filter((_, i) => i !== idx));

  const addRow = (type) => {
    const meta = filterMeta(type);
    const value = meta.kind === 'bool' ? 'true' : meta.kind === 'enum' ? meta.choices[0][0] : '';
    onChange([...filters, { type, value }]);
  };

  const renderValue = (f, idx) => {
    const meta = filterMeta(f.type);
    if (!meta) return null;

    if (meta.kind === 'lookup') {
      const list = options?.[meta.options] || [];
      return (
        <select className="form-select" value={f.value} onChange={(e) => setRow(idx, { value: e.target.value })}>
          <option value="">{isRtl ? '— اختر —' : '— Select —'}</option>
          {list.map((o) => (
            <option key={o.id} value={o.id}>
              {meta.options === 'discounts'
                ? `${isRtl ? o.labelAr : o.labelEn}${o.isCurrentlyActive ? '' : isRtl ? ' (غير فعّال الآن)' : ' (not active now)'}`
                : isRtl ? o.nameAr || o.nameEn : o.nameEn}
            </option>
          ))}
        </select>
      );
    }
    if (meta.kind === 'enum') {
      return (
        <select className="form-select" value={f.value} onChange={(e) => setRow(idx, { value: e.target.value })}>
          {meta.choices.map(([v, en, ar]) => (
            <option key={v} value={v}>
              {isRtl ? ar : en}
            </option>
          ))}
        </select>
      );
    }
    if (meta.kind === 'number') {
      return (
        <input
          className="form-input"
          type="number"
          min="0"
          step="0.01"
          dir="ltr"
          value={f.value}
          onChange={(e) => setRow(idx, { value: e.target.value })}
        />
      );
    }
    return (
      <select className="form-select" value={f.value} onChange={(e) => setRow(idx, { value: e.target.value })}>
        <option value="true">{isRtl ? 'نعم' : 'Yes'}</option>
        <option value="false">{isRtl ? 'لا' : 'No'}</option>
      </select>
    );
  };

  const hasPromotionType = sectionType === SECTION_TYPE.PROMOTION || sectionType === SECTION_TYPE.FREE_SHIPPING;

  return (
    <div className="cs-filters">
      {filters.length === 0 ? (
        <div className="cs-hint">
          {isRtl
            ? 'لا توجد شروط: سيشمل القسم كل المنتجات المتاحة (مرتبة حسب الفرز التلقائي).'
            : 'No conditions: the section covers every available product (ordered by the automatic sort).'}
        </div>
      ) : null}

      {filters.map((f, idx) => (
        <div className="cs-filter-row" key={idx}>
          <select
            className="form-select"
            value={f.type}
            onChange={(e) => {
              const nt = Number(e.target.value);
              const m = filterMeta(nt);
              setRow(idx, { type: nt, value: m.kind === 'bool' ? 'true' : m.kind === 'enum' ? m.choices[0][0] : '' });
            }}
          >
            {FILTER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {label(t)}
              </option>
            ))}
          </select>
          <span className="cs-filter-eq">=</span>
          <div className="cs-filter-value">{renderValue(f, idx)}</div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={() => removeRow(idx)} aria-label={isRtl ? 'حذف الشرط' : 'Remove condition'}>
            ×
          </button>
        </div>
      ))}

      <div className="cs-filter-add">
        <select
          className="form-select"
          value=""
          onChange={(e) => {
            if (e.target.value) addRow(Number(e.target.value));
          }}
        >
          <option value="">{isRtl ? '+ إضافة شرط (AND)' : '+ Add condition (AND)'}</option>
          {FILTER_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {label(t)}
            </option>
          ))}
        </select>
      </div>

      {hasPromotionType ? (
        <div className="cs-hint cs-hint--accent">
          {sectionType === SECTION_TYPE.FREE_SHIPPING
            ? isRtl
              ? 'الشحن المجاني يُطبَّق من قواعد الخصومات الفعلية فقط. المنتجات التي ليس لها قاعدة شحن مجاني لن تظهر هنا.'
              : 'Free shipping is granted only by real discount rules. Products without a free-shipping rule will not appear here.'
            : isRtl
              ? 'السعر الترويجي يأتي من نظام الخصومات، وليس من هذا القسم. أنشئ الخصم أو عدّله من صفحة الخصومات.'
              : 'Promotional prices come from the Discounts system, not from this section. Create or edit the discount on the Discounts page.'}{' '}
          <Link to="/discounts">{isRtl ? 'فتح الخصومات والعروض' : 'Open Discounts & Promotions'}</Link>
        </div>
      ) : null}

      {filters.some((f) => f.type === FILTER.MIN_PRICE || f.type === FILTER.MAX_PRICE) ? (
        <div className="cs-hint">
          {isRtl
            ? 'الأسعار بالدولار كما هي مخزنة في الكتالوج، وتُقارَن بأقل سعر لأحجام المنتج المتاحة.'
            : 'Prices are the catalog USD prices, compared against each product\'s cheapest enabled size. No exchange rates are involved.'}
        </div>
      ) : null}
    </div>
  );
};

export default FilterEditor;
